import React from 'react'
import { SessionFilters, Department, DEPARTMENT_LABELS } from '../types/communication.types'

interface SessionFiltersProps {
  filters: SessionFilters
  onFiltersChange: (filters: Partial<SessionFilters>) => void
  totalSessions: number
  unreadCount: number
  connectedCount: number
}

export const SessionFiltersComponent: React.FC<SessionFiltersProps> = ({
  filters,
  onFiltersChange,
  totalSessions,
  unreadCount,
  connectedCount
}) => {
  const departments: Department[] = ['recepcao', 'financeiro', 'turismo', 'outro']

  const handleDepartmentToggle = (department: Department) => {
    // 🎯 SELEÇÃO EXCLUSIVA: Apenas 1 departamento pode estar selecionado por vez
    // Se clicar no mesmo departamento que já está selecionado, desmarca
    // Se clicar em outro departamento, substitui o anterior
    const isCurrentlySelected = filters.departments.includes(department)
    const newDepartments = isCurrentlySelected ? [] : [department]
    
    onFiltersChange({ departments: newDepartments })
  }

  const clearAllFilters = () => {
    onFiltersChange({
      showUnreadOnly: false,
      showConnectedOnly: false,
      departments: [],
      searchQuery: ''
    })
  }

  const hasActiveFilters = 
    filters.showUnreadOnly || 
    filters.showConnectedOnly || 
    filters.departments.length > 0 || 
    filters.searchQuery.trim().length > 0

  return (
    <div className="card mb-4">
      <div className="card-body p-4">
        {/* Campo de busca */}
        <div className="mb-4">
          <div className="position-relative">
            <i className="fas fa-search position-absolute start-0 top-50 translate-middle-y ms-3 text-muted"></i>
            <input
              type="text"
              className="form-control form-control-sm ps-10"
              placeholder="Buscar por nome, quarto ou mensagem..."
              value={filters.searchQuery}
              onChange={(e) => onFiltersChange({ searchQuery: e.target.value })}
            />
            {filters.searchQuery && (
              <button
                className="btn btn-sm btn-icon position-absolute end-0 top-50 translate-middle-y me-2"
                onClick={() => onFiltersChange({ searchQuery: '' })}
                title="Limpar busca"
              >
                <i className="fas fa-times text-muted"></i>
              </button>
            )}
          </div>
        </div>

        {/* Filtros rápidos */}
        <div className="mb-4">
          <h6 className="fw-bold text-gray-800 mb-3">Filtros Rápidos</h6>
          
          <div className="d-flex flex-wrap gap-2">
            {/* Filtro: Apenas não lidas */}
            <label className="form-check form-check-custom form-check-sm">
              <input
                className="form-check-input"
                type="checkbox"
                checked={filters.showUnreadOnly}
                onChange={(e) => onFiltersChange({ showUnreadOnly: e.target.checked })}
              />
              <span className="form-check-label fw-semibold text-gray-700">
                Não lidas ({unreadCount})
              </span>
            </label>

            {/* Filtro: Apenas conectadas */}
            <label className="form-check form-check-custom form-check-sm">
              <input
                className="form-check-input"
                type="checkbox"
                checked={filters.showConnectedOnly}
                onChange={(e) => onFiltersChange({ showConnectedOnly: e.target.checked })}
              />
              <span className="form-check-label fw-semibold text-gray-700">
                Conectadas ({connectedCount})
              </span>
            </label>
          </div>
        </div>

        {/* Filtros por departamento */}
        <div className="mb-4">
          <h6 className="fw-bold text-gray-800 mb-3">Departamentos</h6>
          
          <div className="d-flex flex-wrap gap-2">
            {departments.map(department => {
              const isSelected = filters.departments.includes(department)
              
              return (
                <button
                  key={department}
                  className={`btn btn-sm ${
                    isSelected 
                      ? 'btn-primary' 
                      : 'btn-light-primary'
                  }`}
                  onClick={() => handleDepartmentToggle(department)}
                >
                  <i className={`fas ${getDepartmentIcon(department)} me-1`}></i>
                  {DEPARTMENT_LABELS[department]}
                </button>
              )
            })}
          </div>
        </div>

        {/* Estatísticas e ações */}
        <div className="d-flex justify-content-between align-items-center">
          <div className="text-muted fs-7">
            Total: {totalSessions} sessões
            {hasActiveFilters && (
              <span className="ms-2">
                <i className="fas fa-filter text-primary"></i>
                Filtros ativos
              </span>
            )}
          </div>

          {hasActiveFilters && (
            <button
              className="btn btn-sm btn-light-danger"
              onClick={clearAllFilters}
            >
              <i className="fas fa-times me-1"></i>
              Limpar Filtros
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Função auxiliar para ícones dos departamentos
const getDepartmentIcon = (department: Department): string => {
  const icons: Record<Department, string> = {
    recepcao: 'fa-concierge-bell',
    financeiro: 'fa-calculator',
    turismo: 'fa-map-marked-alt',
    copa: 'fa-utensils',
    restaurante: 'fa-utensils',
    outro: 'fa-question-circle'
  }
  
  return icons[department] || 'fa-question-circle'
}